// Ranní přehled na Domů (F4 4.10 „Ranní přehled a hlídač lhůt — signály“; docs/11 §7 bod 1) — nad dlaždicemi sekcí:
//   - `rezim_popis` ze serveru („Dispečer — jízdy, kontrola, požadavky“, „Řidič — váš den“…),
//   - dlaždice ze serveru (prototyp `homeTiles`, `homeTileStyle(alert)`: popisek 11/800, hodnota 16/800, podtext 12,
//     `alert` = červený rámeček; klik = `odkaz`) — texty i viditelnost určuje server podle role,
//   - Dnešní agenda (čas · nadpis · podtext · stav → odkaz; „Dnes nic naplánovaného“),
//   - Souhrn dne (prototyp „Ranní souhrn AI“: `shrnuti` s „sestaveno v HH:MM“, jinak `fakta` jako odrážky
//     a „Sestavit souhrn dne (AI)“ / „Sestavuji…“ / „Sestavit znovu“; s `shrnuti_duvod` věta a tlačítko s důvodem),
//   - Signály (≤ 10 nejzávažnějších, „Všechny signály (N)“ → drawer, „Nic nehoří“; „Přepočítat teď“ jen s akcí
//     `signal.prepocitat` — kdo ji nemá, tlačítko nevidí),
//   - Neodeslané zápisy v tabletech (05/136): signály `neodeslane_zapisy` ve vlastním bloku nad souhrnem — kdo skončil
//     směnu s neodeslanými zápisy, co dělat, Vyřešeno; zmizí, až tablet frontu odešle,
//   - Dnes nepřítomní (05/242): jen kancelář — „Dovolená: …“, „Nemoc: …“, „Lékař: … (8:00–10:30)“; druh dává server
//     (přesný důvod kanceláři — rozhodnutí 28. 9. 2026 k 05/1140), obrazovka nic nedomýšlí.
// Účet bez role / role bez přehledu: server vrátí prázdné dlaždice s podtextem — vykreslí se, co přijde.
// Chyba načtení → `dlazdice__chyba` + „Zkusit znovu“ (jako stav serveru). Všechna čísla spočítal server.
// Odkaz dlaždice, agendy a signálu se nevykreslí, když účet cílovou sekci nemá (`odkazy.ts`) — dlaždice zůstane bez odkazu.
import type { Ja } from '@ecoser/shared';
import { useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { duvodChyby, naChybu } from '../api/klient';
import {
  AKCE_PREPOCITAT_SIGNALY,
  klicPrehledu,
  nejzavaznejsi,
  obnovitPrehled,
  popisPrepoctu,
  type PrehledAgenda,
  type PrehledDlazdice,
  prepocitatSignaly,
  type RanniPrehled as RanniPrehledData,
  sestavitShrnuti,
} from '../api/prehled';
import { formatCas } from '../formaty';
import { Nacitani } from '../komponenty/Nacitani';
import { lina, NacitaniCasti } from '../komponenty/NacitaniCasti';
import { PrazdnyStav } from '../komponenty/PrazdnyStav';
import { maAkci } from '../komponenty/prava';
import { TlacitkoSDuvodem } from '../komponenty/TlacitkoSDuvodem';
import { useToast } from '../komponenty/Toast';
import { smiOtevritOdkaz } from './odkazy';
import { SignalRadek } from './SignalRadek';

// Zásuvka „Všechny signály“ se otevře jen na klepnutí — její kód se stáhne až tehdy (rozpočet hlavního balíku).
const VsechnySignaly = lina(() => import('./VsechnySignaly').then((m) => m.VsechnySignaly));

/** Smí přihlášený „Přepočítat teď“: akce `signal.prepocitat` z /ja (08 §3: admin a jednatel); server ji hlídá také. */
export function smiPrepocitat(ja: Ja): boolean {
  return maAkci(ja.akce, AKCE_PREPOCITAT_SIGNALY);
}

/** Dlaždice ze serveru (prototyp `homeTiles`): klik vede na `odkaz`; `alert` = červený rámeček a hodnota. */
function Dlazdice({ d, ja }: { d: PrehledDlazdice; ja: Ja }) {
  const trida = `karta domu__dlazdice-polozka${d.alert ? ' domu__dlazdice-polozka--alert' : ''}`;
  const obsah = (
    <>
      <span className="domu__dlazdice-kategorie">{d.popisek}</span>
      <span className="domu__dlazdice-nazev" data-testid="dlazdice-hodnota">
        {d.hodnota}
      </span>
      <span className="domu__dlazdice-podtext">{d.podtext ?? ''}</span>
    </>
  );
  return d.odkaz && smiOtevritOdkaz(ja, d.odkaz) ? (
    <Link to={d.odkaz} className={trida} data-testid={`dlazdice-${d.klic}`} data-alert={d.alert}>
      {obsah}
    </Link>
  ) : (
    <div className={trida} data-testid={`dlazdice-${d.klic}`} data-alert={d.alert}>
      {obsah}
    </div>
  );
}

function RadekAgendy({ a, ja }: { a: PrehledAgenda; ja: Ja }) {
  const obsah = (
    <>
      <span className="agenda__cas">{a.cas_popis}</span>
      <span className="agenda__obsah">
        <span className="agenda__nadpis">{a.nadpis}</span>
        {a.podtext && <span className="agenda__podtext">{a.podtext}</span>}
      </span>
      {a.stav_popis && <span className="stitek stitek--neutral">{a.stav_popis}</span>}
    </>
  );
  return (
    <li data-testid="agenda-radek" data-entita={a.entita}>
      {a.odkaz && smiOtevritOdkaz(ja, a.odkaz) ? (
        <Link to={a.odkaz} className="agenda__radek">
          {obsah}
        </Link>
      ) : (
        <div className="agenda__radek">{obsah}</div>
      )}
    </li>
  );
}

/** Souhrn dne (prototyp „Ranní souhrn AI“): text z fakt složí asistent, čísla zkontroluje kód na serveru. */
function SouhrnDne({ prehled, datum }: { prehled: RanniPrehledData; datum?: string }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [sestavuji, setSestavuji] = useState(false);
  const maCo =
    prehled.fakta.length > 0 || Boolean(prehled.shrnuti) || Boolean(prehled.shrnuti_duvod);
  if (!maCo) return null;

  const sestavit = async () => {
    setSestavuji(true);
    try {
      const odpoved = await sestavitShrnuti();
      queryClient.setQueryData<RanniPrehledData>(klicPrehledu(datum), (puvodni) =>
        puvodni
          ? {
              ...puvodni,
              shrnuti: odpoved.shrnuti,
              shrnuti_duvod: odpoved.shrnuti_duvod,
              shrnuti_kdy: odpoved.shrnuti_kdy,
              fakta: odpoved.fakta,
            }
          : puvodni,
      );
      if (!odpoved.shrnuti) {
        toast.ukazat(
          odpoved.shrnuti_duvod
            ? `Souhrn se nepodařilo sestavit: ${odpoved.shrnuti_duvod}`
            : 'Souhrn se nepodařilo sestavit.',
        );
      }
    } catch (e) {
      toast.ukazat(`Souhrn se nepodařilo sestavit. ${duvodChyby(e)}`);
    } finally {
      setSestavuji(false);
    }
  };

  const popisekTlacitka = sestavuji
    ? 'Sestavuji…'
    : prehled.shrnuti
      ? 'Sestavit znovu'
      : 'Sestavit souhrn dne (AI)';
  const duvod = !prehled.shrnuti && prehled.shrnuti_duvod ? prehled.shrnuti_duvod : undefined;

  return (
    <section className="karta domu__karta souhrn" aria-labelledby="souhrn-dne-titul">
      <div className="domu__karta-hlavicka">
        <h2 id="souhrn-dne-titul" className="domu__karta-titul">
          Souhrn dne
        </h2>
        {prehled.shrnuti && prehled.shrnuti_kdy && (
          <span className="souhrn__kdy">sestaveno v {formatCas(prehled.shrnuti_kdy)}</span>
        )}
      </div>
      <div className="souhrn__telo">
        <div className="souhrn__obsah">
          {prehled.shrnuti ? (
            <p className="souhrn__text" data-testid="souhrn-text">
              {prehled.shrnuti}
            </p>
          ) : (
            <>
              <p className="souhrn__uvod">
                {duvod
                  ? `Souhrn dne teď není k dispozici: ${duvod}. Fakta zůstávají:`
                  : 'Ranní souhrn dne — AI shrne, co dnes vyžaduje pozornost.'}
              </p>
              {prehled.fakta.length > 0 && (
                <ul className="souhrn__fakta" data-testid="souhrn-fakta">
                  {prehled.fakta.map((f, i) => (
                    <li key={`${i}-${f}`}>{f}</li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
        <TlacitkoSDuvodem
          varianta="sekundarni"
          disabled={sestavuji || Boolean(duvod)}
          duvod={duvod}
          onClick={() => void sestavit()}
          data-testid="sestavit-souhrn"
        >
          {popisekTlacitka}
        </TlacitkoSDuvodem>
      </div>
    </section>
  );
}

/** Neodeslané zápisy v tabletech (05/136): signály `neodeslane_zapisy` ve vlastním bloku, aby se neztratily. */
function NeodeslaneZapisy({ signaly }: { signaly: RanniPrehledData['neodeslane_zapisy'] }) {
  if (!signaly || signaly.length === 0) return null;
  return (
    <section
      className="karta domu__karta prehled__tablety"
      aria-labelledby="tablety-titul"
      data-testid="neodeslane-zapisy"
    >
      <div className="domu__karta-hlavicka">
        <h2 id="tablety-titul" className="domu__karta-titul">
          Neodeslané zápisy v tabletech
          <span className="stitek stitek--warn prehled__pocet">{signaly.length}</span>
        </h2>
      </div>
      <p className="prehled__vysvetleni">
        Tyto směny skončily se zápisy, které zůstaly jen v tabletu — server je nemá, dokud je tablet
        neodešle. Upozornění zmizí samo, jakmile tablet chytí signál.
      </p>
      <ul className="signaly__seznam" aria-label="Neodeslané zápisy v tabletech">
        {signaly.map((s) => (
          <SignalRadek key={s.id} signal={s} />
        ))}
      </ul>
    </section>
  );
}

/** Dnes nepřítomní (05/242): skupiny podle druhu ze serveru, jméno a případně čas od–do. */
function Nepritomni({ seznam, ja }: { seznam: RanniPrehledData['nepritomni']; ja: Ja }) {
  if (!seznam || seznam.length === 0) return null;
  const skupiny = new Map<string, string[]>();
  for (const n of seznam) {
    const jmeno = n.cas_od && n.cas_do ? `${n.jmeno} (${n.cas_od}–${n.cas_do})` : n.jmeno;
    skupiny.set(n.druh_popis, [...(skupiny.get(n.druh_popis) ?? []), jmeno]);
  }
  return (
    <section
      className="karta domu__karta prehled__nepritomni"
      aria-labelledby="nepritomni-titul"
      data-testid="nepritomni"
    >
      <div className="domu__karta-hlavicka">
        <h2 id="nepritomni-titul" className="domu__karta-titul">
          Dnes nepřítomní
        </h2>
        {smiOtevritOdkaz(ja, '/kalendar') && (
          <Link to="/kalendar" className="tlacitko tlacitko--sekundarni tlacitko--radek">
            Kalendář
          </Link>
        )}
      </div>
      <ul className="prehled__nepritomni-seznam">
        {[...skupiny].map(([druh, jmena]) => (
          <li key={druh} data-testid="nepritomni-skupina">
            <strong>{druh}:</strong> {jmena.join(', ')}
          </li>
        ))}
      </ul>
    </section>
  );
}

interface Props {
  ja: Ja;
  prehled: UseQueryResult<RanniPrehledData>;
  datum?: string;
}

export function RanniPrehledBlok({ ja, prehled, datum }: Props) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [drawer, setDrawer] = useState(false);
  const [prepocitavam, setPrepocitavam] = useState(false);
  const data = prehled.data;
  const chyba = prehled.isError ? naChybu(prehled.error) : null;

  const prepocitat = async () => {
    setPrepocitavam(true);
    try {
      const vysledek = await prepocitatSignaly();
      obnovitPrehled(queryClient);
      toast.ukazat(popisPrepoctu(vysledek));
    } catch (e) {
      toast.ukazat(duvodChyby(e));
    } finally {
      setPrepocitavam(false);
    }
  };

  const signaly = data ? nejzavaznejsi(data.signaly) : [];
  const celkem = data?.signaly_celkem ?? data?.signaly.length ?? 0;

  return (
    <section className="prehled" aria-labelledby="ranni-prehled-titul" data-testid="ranni-prehled">
      <div className="prehled__hlava">
        <h2 id="ranni-prehled-titul" className="prehled__titul">
          Ranní přehled
        </h2>
        {data?.rezim_popis && (
          <p className="prehled__rezim" data-testid="prehled-rezim">
            {data.rezim_popis}
          </p>
        )}
      </div>

      {prehled.isPending && <Nacitani text="Načítám přehled…" />}

      {chyba && !data && (
        <div className="dlazdice__chyba" role="alert">
          <p className="pole__chyba">Přehled se nepodařilo načíst. {chyba.zprava}</p>
          <p className="pole__napoveda">Kód: {chyba.kod}</p>
          <button
            type="button"
            className="tlacitko tlacitko--sekundarni tlacitko--radek"
            onClick={() => void prehled.refetch()}
            disabled={prehled.isFetching}
          >
            Zkusit znovu
          </button>
        </div>
      )}

      {data && (
        <>
          {data.dlazdice.length > 0 && (
            <div className="domu__dlazdice" role="list" aria-label="Dnešní čísla">
              {data.dlazdice.map((d) => (
                <div key={d.klic} role="listitem" className="domu__dlazdice-bunka">
                  <Dlazdice d={d} ja={ja} />
                </div>
              ))}
            </div>
          )}

          <NeodeslaneZapisy signaly={data.neodeslane_zapisy} />
          <Nepritomni seznam={data.nepritomni} ja={ja} />

          <SouhrnDne prehled={data} datum={datum} />

          <div className="prehled__sloupce">
            <section className="karta domu__karta" aria-labelledby="agenda-titul">
              <div className="domu__karta-hlavicka">
                <h2 id="agenda-titul" className="domu__karta-titul">
                  Dnešní agenda
                </h2>
              </div>
              {data.agenda.length === 0 ? (
                <PrazdnyStav text="Dnes nic naplánovaného." />
              ) : (
                <ul className="agenda">
                  {data.agenda.map((a, i) => (
                    <RadekAgendy
                      key={`${a.entita_id ?? i}-${a.cas_popis}-${a.nadpis}`}
                      a={a}
                      ja={ja}
                    />
                  ))}
                </ul>
              )}
            </section>

            <section className="karta domu__karta" aria-labelledby="signaly-titul">
              <div className="domu__karta-hlavicka domu__karta-hlavicka--zalamovat">
                <h2 id="signaly-titul" className="domu__karta-titul">
                  Signály
                  {celkem > 0 && (
                    <span
                      className="stitek stitek--danger prehled__pocet"
                      data-testid="signaly-pocet"
                    >
                      {celkem}
                    </span>
                  )}
                </h2>
                {smiPrepocitat(ja) && (
                  <button
                    type="button"
                    className="tlacitko tlacitko--textove tlacitko--radek"
                    onClick={() => void prepocitat()}
                    disabled={prepocitavam}
                    data-testid="prepocitat-signaly"
                  >
                    {prepocitavam ? 'Přepočítávám…' : 'Přepočítat teď'}
                  </button>
                )}
                <button
                  type="button"
                  className="tlacitko tlacitko--sekundarni tlacitko--radek"
                  onClick={() => setDrawer(true)}
                  data-testid="vsechny-signaly"
                >
                  Všechny signály ({celkem})
                </button>
              </div>
              {signaly.length === 0 ? (
                <PrazdnyStav text="Nic nehoří — žádný otevřený signál." />
              ) : (
                <ul className="signaly__seznam" aria-label="Otevřené signály">
                  {signaly.map((s) => (
                    <SignalRadek key={s.id} signal={s} />
                  ))}
                </ul>
              )}
            </section>
          </div>
        </>
      )}

      {drawer && (
        <NacitaniCasti naZavrit={() => setDrawer(false)}>
          <VsechnySignaly naZavrit={() => setDrawer(false)} />
        </NacitaniCasti>
      )}
    </section>
  );
}
